import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import Grid from "@mui/material/Unstable_Grid2";
import SortIcon from "@mui/icons-material/Sort";
import GridViewIcon from "@mui/icons-material/GridView";
import {Add, FileDownloadOutlined} from "@mui/icons-material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import {Typography} from "@mui/material";
import Button from "../../../../components/base/button/button";
import SearchInput from "../../../../components/base/input/search_input";
import Api from "../../../../../core/services/api/api";
import notebookTags, {
    getVendorAllowedNotePageValues,
    getVendorNoteSectionVisibility,
    getVendorNotesAggregateQueryTags,
    NotebookTagKeys,
} from "../../../../../core/models/static/routes/notebook-tags";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import {useAppSelector} from "../../../../redux";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {dateComparator, exportCSVFile} from "../../../../../core/services/utils/utils";
import {buildNotesCsvExportInfo} from "../../../../../core/services/notebook/export-notes";
import DateUtils from "../../../../../core/services/date-utils";
import VendorNotesGridView from "./views/grid";
import VendorNotesListView from "./views/list";
import useAccessLevels from "../../../../hooks/use-access-levels";

/**
 * Strips HTML tags for keyword matching on note body text.
 * @param {string | undefined} html
 * @return {string}
 */
const stripHtml = (html) => (html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Aggregated vendor notes across account-info, purchases, and notes page tags.
 * Loaded with a single ListByTags request (query + application + module), then filtered by access.
 */
const VendorNotesView = () => {
    const defaultVendor = useAppSelector(state => state.vendorInformation);
    const {packages} = useAccessLevels();
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [gridView, setGridView] = useState(true);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const {configureNotebookTags, toggleFabNotes} = useConfigureNotebook('vendor-notes-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-notes'});

    const canManageNotes = packages.base.manageVendorNotes;
    const canExportNotes = packages.base.exportVendorNotes;
    const sectionVisibility = useMemo(() => getVendorNoteSectionVisibility(packages), [
        packages.base.viewVendorAccountInfo,
        packages.base.viewVendorNotes,
        packages.purchasing.viewPurchaseOrders,
        packages.purchasing.viewPurchaseHistory,
    ]);

    const aggregateQueryTags = useMemo(() => {
        if (!defaultVendor?.code) {
            return null;
        }
        return getVendorNotesAggregateQueryTags(defaultVendor.code);
    }, [defaultVendor?.code]);

    const allowedPageValues = useMemo(
        () => new Set(getVendorAllowedNotePageValues(sectionVisibility)),
        [sectionVisibility],
    );

    /**
     * Hides FAB notes on this page.
     */
    useLayoutEffect(() => {
        toggleFabNotes(false);
    }, [toggleFabNotes]);

    /**
     * Configures notebook tags and behavioural analytics for this view.
     */
    useEffect(() => {
        if (!defaultVendor?.code) {
            return;
        }
        configureNotebookTags((tags) => tags.vendorNotes(defaultVendor.code));
        configureBehavioralTool((tags) => tags.vendorNotes(defaultVendor.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultVendor?.code]);

    /**
     * Fetches all notes for the aggregate query tags (single request, expands page size if needed).
     */
    const fetchNotesForQueryTags = useCallback(async (tagGroup) => {
        const size = 20;
        const initialResponse = await Api.getPageNotes({
            tags: tagGroup,
            pagination: {currentPage: 1, pageSize: size},
        });
        if (!isMounted()) {
            return [];
        }
        if (!initialResponse?.resultFlag) {
            return [];
        }
        if (initialResponse?.data?.totalCount > size) {
            const fullResponse = await Api.getPageNotes({
                tags: tagGroup,
                pagination: {
                    currentPage: 1,
                    pageSize: Math.max(initialResponse?.data?.totalCount ?? 0, 1),
                },
            });
            if (!isMounted()) {
                return [];
            }
            return fullResponse?.data?.items ?? [];
        }
        return initialResponse?.data?.items ?? [];
    }, [isMounted]);

    /**
     * Loads vendor notes once, then keeps only sections the operator can access.
     */
    const loadNotes = useCallback(async () => {
        if (!aggregateQueryTags?.length || !allowedPageValues.size) {
            setNotes([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const items = await fetchNotesForQueryTags(aggregateQueryTags);
        if (!isMounted()) {
            return;
        }
        const filtered = items.filter((note) => {
            const noteTags = note?.tagList ?? note?.tags ?? [];
            const pageTag = noteTags.find((tag) => tag.key === NotebookTagKeys.page);
            return pageTag?.value && allowedPageValues.has(pageTag.value);
        }).sort((a, b) => dateComparator(
            b.modifiedDateTime ?? b.createdDateTime,
            a.modifiedDateTime ?? a.createdDateTime,
        ));
        setNotes(filtered);
        setLoading(false);
    }, [aggregateQueryTags, allowedPageValues, fetchNotesForQueryTags, isMounted]);

    useEffect(() => {
        void loadNotes();
    }, [loadNotes]);

    const filteredNotes = useMemo(() => {
        const normalizedKeyword = keyword.trim().toLowerCase();
        if (!normalizedKeyword) {
            return notes;
        }
        return notes.filter((note) => {
            const title = note?.title?.toLowerCase() ?? '';
            const body = stripHtml(note?.body).toLowerCase();
            return title.includes(normalizedKeyword) || body.includes(normalizedKeyword);
        });
    }, [keyword, notes]);

    const addNewNote = useCallback(() => {
        if (!defaultVendor?.code) {
            return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: null,
            tagList: notebookTags.vendorNotes(defaultVendor.code),
            onSuccess: (note) => setNotes((prevState) => [note, ...prevState]),
        }));
    }, [defaultVendor?.code, dispatch]);

    const openUpsertDialog = useCallback((note) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: note,
            onSuccess: (updatedNote) => setNotes((prevState) =>
                prevState.map((entry) => entry.id === updatedNote.id ? updatedNote : entry),
            ),
        }));
    }, [dispatch]);

    const toggleNoteIsPrivate = useCallback(async (note, isPrivate) => {
        const forApi = {
            noteId: note.id,
            title: note.title,
            body: note.body,
            isPrivate: isPrivate,
            tagList: note.tagList,
            updatedProperties: ['IsPrivate'],
        };
        const response = await Api.updateNote(forApi);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setNotes((prevState) => prevState.map((entry) =>
                entry.id === note.id ? {...entry, isPrivate: isPrivate} : entry,
            ));
        }
        return response;
    }, [isMounted]);

    const removeNote = useCallback(async (noteId) => {
        const response = await Api.removeNote(noteId);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setNotes((prevState) => prevState.filter((entry) => entry.id !== noteId));
        }
    }, [isMounted]);

    const setNote = useCallback((noteId, updatedNote) => {
        setNotes((prevState) => prevState.map((entry) => entry.id === noteId ? updatedNote : entry));
    }, []);

    const searchOverRecords = useCallback((value) => {
        setKeyword(value ?? '');
    }, []);

    const changeView = useCallback((view) => {
        setGridView(view !== 'list');
    }, []);

    const notesExportColumns = useMemo(() => ({
        title: t('list.columns.title'),
        operator: t('list.columns.operator'),
        modifiedDate: t('list.columns.modifiedDate'),
        source: t('list.columns.source'),
        visibility: t('list.columns.visibility'),
        body: t('list.columns.note'),
        publicVisibility: t('list.visibility.public'),
        privateVisibility: t('list.visibility.private'),
    }), [t]);

    /**
     * Exports filtered vendor notes to CSV (includes HTML body).
     */
    const exportNotesToCsv = useCallback(async () => ({
        ...buildNotesCsvExportInfo(filteredNotes, {
            title: `${t('exportTitle')} - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`,
            columns: notesExportColumns,
        }),
    }), [filteredNotes, notesExportColumns, t]);

    /**
     * Triggers CSV download (grid view header button).
     */
    const downloadNotesCsv = useCallback(async () => {
        const exportInfo = await exportNotesToCsv();
        if (!exportInfo?.body?.length) {
            return;
        }
        exportCSVFile(exportInfo.body, exportInfo.title);
    }, [exportNotesToCsv]);

    const toggleButtonGroupMixin = useCallback((theme) => ({
        border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
        '& .MuiToggleButton-root': {
            backgroundColor: 'transparent',
            borderColor: 'transparent !important',
            color: theme.palette.text.primary,
            height: 30,
            marginBlock: 0.5,
            marginInline: 0.5,
            borderRadius: 1,
            paddingBlock: 0.5,
            paddingInline: 0.5,
        },
        '& .MuiToggleButton-root:hover': {
            backgroundColor: theme.palette.background.tertiary,
            borderColor: `${theme.palette.background.tertiary} !important`,
            color: theme.palette.text.primary,
        },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: theme.palette.background.tertiary,
            borderColor: `${theme.palette.background.tertiary} !important`,
            color: theme.palette.primary.main,
        },
        '& .MuiToggleButton-root.Mui-selected:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.background.tertiary,
            borderColor: `${theme.palette.background.tertiary} !important`,
        },
    }), []);

    return (
        <Grid container spacing={2} sx={{padding: 2}}>
            <Grid container columns={12} xs={12} alignItems={'center'}>
                <Grid xs>
                    <Typography variant={'h6'} fontWeight={600}>
                        {t('title')}
                    </Typography>
                </Grid>
                <Grid xs={'auto'} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                    {
                        canExportNotes &&
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={downloadNotesCsv}
                            disabled={loading || filteredNotes.length < 1}
                        >
                            <FileDownloadOutlined className={'icon small'} style={{margin: '2px 0'}}/>
                            {t('exportButton')}
                        </Button>
                    }
                    {
                        canManageNotes &&
                        <Button
                            className={'d-flex align-items-center justify-content-center'}
                            variant={'outlined'}
                            color={'primary'}
                            onClick={addNewNote}
                            disabled={loading || !defaultVendor?.code}
                        >
                            <Add className={'icon small'} style={{margin: '2px 0'}}/>
                            {t('addNote')}
                        </Button>
                    }
                    <ToggleButtonGroup
                        orientation="horizontal"
                        exclusive
                        onChange={(_event, value) => value && changeView(value)}
                        sx={toggleButtonGroupMixin}
                    >
                        <ToggleButton selected={!gridView} value={'list'} aria-label={t('listView')}>
                            <SortIcon/>
                        </ToggleButton>
                        <ToggleButton selected={gridView} value={'grid'} aria-label={t('gridView')}>
                            <GridViewIcon/>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            <Grid xs={12}>
                <SearchInput
                    initialValue={keyword}
                    loading={loading}
                    autoSearch
                    immediateSearch
                    search={searchOverRecords}
                    label={t('searchLabel')}
                />
            </Grid>
            <Grid xs={12}>
                {
                    gridView &&
                    <VendorNotesGridView
                        notes={filteredNotes}
                        loading={loading}
                        toggleNoteIsPrivate={canManageNotes ? toggleNoteIsPrivate : undefined}
                        setNote={canManageNotes ? setNote : undefined}
                        removeNote={canManageNotes ? removeNote : undefined}
                    />
                }
                {
                    !gridView &&
                    <VendorNotesListView
                        notes={filteredNotes}
                        loading={loading}
                        updateNote={canManageNotes ? openUpsertDialog : undefined}
                        removeNote={canManageNotes ? removeNote : undefined}
                        getExportInfo={canExportNotes ? exportNotesToCsv : undefined}
                    />
                }
            </Grid>
        </Grid>
    );
};

export default VendorNotesView;
